import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Interfaz para provincia
 */
export interface Province {
  id: string;
  code: string;
  name: string;
  countryId: string;
  status: string;
}

/**
 * Tipo de retorno para paginación server-side
 */
export interface ProvincePageResponse {
  items: Province[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class ProvinceService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  /**
   * Normaliza el status de mayúsculas a minúsculas
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToProvince(item: any): Province {
    return {
      id: item.id || '',
      code: item.code || '',
      name: item.name || '',
      countryId: item.countryId || '',
      status: this.normalizeStatus(item.status),
    };
  }

  /**
   * Obtiene todas las provincias de un país (con paginación)
   */
  getAll(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'desc' | 'asc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<ProvincePageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    return this.httpClient
      .get<
        ApiResponse<PageableData>
      >(`${this.apiUrl}/${countryId}/provinces`, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((item: any) => this.mapToProvince(item)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Obtiene una provincia por ID
   */
  getById(countryId: string, provinceId: string): Observable<Province> {
    return this.httpClient
      .get<
        ApiResponse<any>
      >(`${this.apiUrl}/${countryId}/provinces/${provinceId}`)
      .pipe(map((response) => this.mapToProvince(response.data)));
  }

  /**
   * Crea una nueva provincia
   */
  create(
    countryId: string,
    data: { code: string; name: string },
  ): Observable<{ message: string }> {
    return this.httpClient
      .post<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/provinces`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Provincia creada exitosamente',
        })),
      );
  }

  /**
   * Actualiza una provincia existente
   */
  update(
    countryId: string,
    provinceId: string,
    data: { code: string; name: string },
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/provinces/${provinceId}`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Provincia actualizada exitosamente',
        })),
      );
  }

  /**
   * Elimina una provincia
   */
  delete(
    countryId: string,
    provinceId: string,
  ): Observable<{ message: string }> {
    return this.httpClient
      .delete<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/provinces/${provinceId}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Provincia eliminada exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de una provincia
   */
  updateStatus(
    countryId: string,
    provinceId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/provinces/${provinceId}/status`, {
        status: status.toUpperCase(),
      })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }
}
